import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, KeyRound, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

export default function ResetPassword() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = useMemo(() => params.get('token') || '', [params]);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) setError(t('auth.reset_no_token'));
    }, [token, t]);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (password.length < 6) {
            setError(t('auth.reset_min_length'));
            return;
        }
        if (password !== confirm) {
            setError(t('auth.reset_no_match'));
            return;
        }
        setLoading(true);
        try {
            await api.post('/auth/reset-password', { token, password });
            setDone(true);
            // Auto-redirect to login after 2.5s
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            setError(err.response?.data?.detail || t('auth.reset_generic_error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[hsl(220_13%_8%)] text-white">
            <header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-white/5">
                <Link
                    to="/login"
                    aria-label={t('auth.back_to_login')}
                    className="inline-flex items-center justify-center h-10 w-10 rounded-full text-white/70 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0"
                    data-testid="reset-back-link"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="flex-1 flex justify-center min-w-0">
                    <Logo variant="light" />
                </div>
                <div className="flex-shrink-0">
                    <LanguageSwitcher variant="dark" compact />
                </div>
            </header>

            <div className="flex-1 flex items-center justify-center px-4 py-6 sm:py-8">
                <Card className="w-full max-w-md bg-[hsl(220_13%_10%)] border-[hsl(220_10%_22%)] text-white p-8">
                    {done ? (
                        <>
                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[hsl(145_55%_34%)]/15 mb-4">
                                <CheckCircle2 className="w-6 h-6 text-[hsl(145_55%_50%)]" />
                            </div>
                            <h1 className="font-display text-3xl mb-2">{t('auth.reset_done_title')}</h1>
                            <p className="text-sm text-white/70 mb-6">{t('auth.reset_done_body')}</p>
                            <Link to="/login" data-testid="reset-done-go-login">
                                <Button className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11">
                                    {t('auth.login_button')}
                                </Button>
                            </Link>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[hsl(var(--accent))]/10 mb-4">
                                <KeyRound className="w-6 h-6 text-[hsl(var(--accent))]" />
                            </div>
                            <h1 className="font-display text-3xl mb-2">{t('auth.reset_title')}</h1>
                            <p className="text-sm text-white/65 mb-6">{t('auth.reset_description')}</p>

                            {error && (
                                <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-[hsl(0_84%_55%)]/10 border border-[hsl(0_84%_55%)]/30" data-testid="reset-error">
                                    <AlertCircle className="w-4 h-4 mt-0.5 text-[hsl(0_84%_70%)] flex-shrink-0" />
                                    <p className="text-xs text-[hsl(0_84%_85%)]">{error}</p>
                                </div>
                            )}

                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label className="text-white/90">{t('auth.reset_new_password')}</Label>
                                    <div className="relative mt-1">
                                        <Input
                                            type={showPwd ? 'text' : 'password'}
                                            required
                                            minLength={6}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 pr-10"
                                            data-testid="reset-password-input"
                                        />
                                        <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60">
                                            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-white/90">{t('auth.reset_confirm_password')}</Label>
                                    <Input
                                        type={showPwd ? 'text' : 'password'}
                                        required
                                        minLength={6}
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="bg-[hsl(220_10%_16%)] border-[hsl(220_10%_22%)] text-white h-11 mt-1"
                                        data-testid="reset-confirm-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading || !token || password.length < 6 || password !== confirm}
                                    className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11 disabled:opacity-50"
                                    data-testid="reset-submit-button"
                                >
                                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('auth.saving')}</> : t('auth.reset_submit')}
                                </Button>
                            </form>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
}
